import { UIElement } from '../ui-element.js';
import styles from './ui-grid.css';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIGrid
 * @element ui-grid
 * @classdesc Represents a class for <code>ui-grid</code> element.
 * This element creates a CSS flex- or CSS grid-enabled grid-like layout.
 * @property {("flex" | "grid")} [layout="flex"] {@attr layout} Layout type. Only applicable on desktop.
 *  {@desc grid}
 * @property {("row" | "column")} [direction="row"] {@attr direction} Direction.
 *  {@desc column}
 * @property {string} [colwidth="25%"] {@attr colwidth} Columns width in percent. Only applicable on desktop for flex layout, as well as grid layout with direction=row.
 * @property {number} [rows="2"] {@attr rows} Rows count. Only applicable on desktop with layout=grid and direction=column.
 * @property {number} [gap="0"] {@attr gap} Gap between grid items.
 * @property {boolean} [isList] {@attr is-list} If true, set the grid and its immediate children as a semantic list.
 * @property {("left" | "center" | "right" | "space-between" | "space-around")} [halign="center"] {@attr halign} Columns horizontal alignment. Only applicable on desktop with layout=flex.
 *  {@desc left}
 *  {@desc center}
 *  {@desc right}
 *  {@desc space-between}
 *  {@desc space-around}
 * @slot {@type "ui-griditem"}
 * @example
 * <ui-grid colwidth="25%">
 * </ui-grid>
 */
class UIGrid extends UIElement {
    /**
     * @type {Props}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                layout: { type: String, default: 'flex' },
                rows: { type: Number, default: 2 },
                gap: Number,
                direction: { type: String, default: 'row' },
                colwidth: { type: String, default: '25%' },
                halign: { type: String, default: 'center' },
                isList: Boolean,
            },
        };
    }

    static get layout() {
        return {
            Flex: 'flex',
            Grid: 'grid',
        };
    }

    static get halign() {
        return {
            Center: 'center',
            Left: 'left',
            Right: 'right',
            SpaceBetween: 'space-between',
            SpaceAround: 'space-around',
        };
    }

    static get direction() {
        return {
            Row: 'row',
            Column: 'column',
        };
    }

    /**
     * @inheritDoc
     */
    observeAttributes() {
        const colwidth = parseFloat(
            this.colwidth.endsWith('%')
                ? this.colwidth.slice(0, -1)
                : this.colwidth
        );
        // Ensure colwidth is a valid number and at least 1
        const safeColwidth = Math.max(
            Number.isNaN(colwidth) ? 25 : colwidth,
            1
        );
        const colnumber = Math.floor(100 / safeColwidth);
        this.style.setProperty('--ui-grid--colwidth', safeColwidth + '%');

        this.style.setProperty('--ui-grid--colnumber', colnumber);

        // The CSS calc() function would not let us to substract two unitless numbers, so we supply it from JS
        this.style.setProperty('--ui-grid--row-gapnumber', colnumber - 1);

        this.style.setProperty('--ui-grid--gap-multiplier', this.gap);

        if (this.layout === 'grid' && this.direction === 'column') {
            this.style.setProperty('--ui-grid--rows', this.rows);
        }

        if (this.isList) {
            this.setAttribute('role', 'list');
            this.querySelectorAll('ui-griditem').forEach((item) => {
                item.setAttribute('role', 'listitem');
            });
        }
    }

    /**
     * @inheritDoc
     */
    render() {}

    /**
     * @inheritDoc
     */
    hydrate() {}
}

UIGrid.defineElement('ui-grid', styles);
export { UIGrid };
